import type { ImpersonationState } from "@/types/auth/auth";

const IMPERSONATION_KEY = "admin_impersonation_state";

export class ImpersonationService {
  static async startImpersonation(adminUserId: string, targetUserId: string, redirectTo?: string): Promise<void> {
    // If we're already impersonating, keep the original admin ID as source of truth.
    const existingAdminUserId = this.getAdminUserId();
    const resolvedAdminUserId = existingAdminUserId || adminUserId;

    const state: ImpersonationState = {
      adminUserId: resolvedAdminUserId,
      impersonatedUserId: targetUserId,
      impersonatedAt: Date.now(),
    };

    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));

    // Clear user-scoped caches that can make the UI appear stuck.
    localStorage.removeItem("userAgents");

    // Force a fresh navigation so the app fully re-hydrates with new user context.
    const target = redirectTo || "/genie-hub/my-genies";
    const separator = target.includes("?") ? "&" : "?";
    window.location.assign(`${target}${separator}impersonation_switch=${Date.now()}`);
  }

  static clearImpersonationState(): void {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    localStorage.removeItem("userAgents");
  }

  static stopImpersonation(redirectTo?: string): void {
    this.clearImpersonationState();

    if (redirectTo) {
      window.location.assign(redirectTo);
    } else {
      window.location.reload();
    }
  }

  static getImpersonationState(): ImpersonationState | null {
    try {
      const stored = sessionStorage.getItem(IMPERSONATION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      // sessionStorage is inaccessible in some embedded/cross-origin contexts (e.g. iframe embeds)
      return null;
    }
  }

  static isImpersonating(): boolean {
    return this.getImpersonationState() !== null;
  }

  static getImpersonatedUserId(): string | null {
    return this.getImpersonationState()?.impersonatedUserId ?? null;
  }

  static getAdminUserId(): string | null {
    return this.getImpersonationState()?.adminUserId ?? null;
  }
}
